import { LitElement, css, html } from "lit";

import { customElement, state } from "lit/decorators.js";

import { appContext } from "ui-common/appContext.ts";
import { rpc } from "ui-common/services/rpc";
import "ui-components/button/wow-button.ts";

import { NotificationService } from "ui-components/notification";
import "../components/wow-logo-full/wow-logo-full";
import { navigateTo } from "../router";
import { t } from "../translations/i18n";
import { queryClient } from "ui-common/data-query-controller";
import { profileDataQueryKey } from "../queries/profile-data";

const styles = css`
  :host {
    display: block;
    min-height: 100dvh;
    background-color: var(--sds-color-background-default-default);
    color: var(--sds-color-text-default-default);
    font-family: var(--sds-typography-family-body);
    box-sizing: border-box;
  }

  * {
    box-sizing: border-box;
  }

  .container {
    width: 100%;
    max-width: 393px;
    margin: 0 auto;
    padding: var(--sds-size-space-800);
    min-height: 100dvh;
    display: flex;
    flex-direction: column;
    gap: var(--sds-size-space-800);
    box-sizing: border-box;
  }

  .logo {
    margin-bottom: var(--sds-size-space-1600);
  }

  .content {
    background-color: var(--sds-color-background-neutral-strong);
    padding: var(--sds-size-space-800) var(--sds-size-space-500);
    border-radius: var(--sds-size-border-radius-200);
    display: flex;
    flex-direction: column;
    gap: var(--sds-size-space-800);
    text-align: center;
    align-items: center;
    box-sizing: border-box;
  }

  .title {
    font: var(--sds-typography-weight-medium) var(--sds-typography-scale-06) / 1.2 var(--sds-typography-family-title);
    color: var(--sds-color-text-default-inverse);
    margin: 0;
    width: 100%;
  }

  .message {
    font-family: var(--sds-typography-family-body);
    font-weight: var(--sds-typography-weight-regular);
    font-size: var(--sds-typography-scale-03);
    line-height: 1.5;
    color: var(--sds-color-text-default-inverse);
    margin: 0;
    width: 100%;
  }

  .error-message {
    color: var(--sds-color-text-danger-default);
  }

  .success-button {
    width: 100%;
    margin-top: var(--sds-size-space-400);
  }

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--sds-color-background-neutral-secondary);
    border-top: 3px solid var(--sds-color-text-default-inverse);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

type VerificationState = "loading" | "success" | "error";

@customElement("email-confirmation-page")
export class EmailConfirmationPage extends LitElement {
  static override styles = [styles];

  @state()
  private verificationState: VerificationState = "loading";

  @state()
  private errorMessage = "";

  constructor() {
    super();
    this.verifyEmail();
  }

  private async verifyEmail() {
    try {
      console.log("[email-confirmation] verifyEmail called, URL:", window.location.href);
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get("token");
      const email = urlParams.get("email");
      const mode = urlParams.get("mode");
      const id = urlParams.get("id");

      console.log("[email-confirmation] URL params:", { token, email, mode, id });

      if (!token || !email) {
        this.verificationState = "error";
        this.errorMessage = "Missing verification parameters";
        return;
      }

      const decodedEmail = decodeURIComponent(email);
      const decodedToken = decodeURIComponent(token);

      if (mode === "change" && id) {
        return this.verifyChangeEmail(id, decodedEmail, decodedToken);
      }

      await new Promise((resolve) => setTimeout(resolve, 2000));

      const { accessToken } = await rpc("verifyEmail", { email: decodedEmail, token: decodedToken });
      appContext.signIn(accessToken);

      this.verificationState = "success";
    } catch (error) {
      console.error("Verification error:", error);
      this.verificationState = "error";
      this.errorMessage = error instanceof Error ? error.message : "Verification failed";
    }
  }

  private async verifyChangeEmail(userId: string, email: string, token: string) {
    if (!(email && token)) return;

    await rpc("changeEmail", { userId, email, token });
    await queryClient.invalidateQueries({ queryKey: [profileDataQueryKey] });
    NotificationService.success(`Your email has been successfully updated to ${email}`);
    if (appContext.currentUser?.handle) return navigateTo("profile");
    navigateTo("sign-in");
  }

  private handleContinue() {
    // Only allow redirect if verification was successful
    if (this.verificationState === "success") {
      this.redirectPostVerification();
    }
  }

  private redirectPostVerification() {
    window.location.href = "/explore";
  }

  private renderContent() {
    switch (this.verificationState) {
      case "loading":
        return html`
          <div class="loading-spinner"></div>
          <h1 class="title">${t("emailConfirmation.verifying")}</h1>
          <p class="message">${t("emailConfirmation.pleaseWait")}</p>
        `;

      case "success":
        return html`
          <h1 class="title">${t("emailConfirmation.success")}</h1>
          <p class="message">${t("emailConfirmation.successMessage")}</p>
          <wow-button class="success-button" variant="primary" size="medium" @click=${this.handleContinue}>
            ${t("emailConfirmation.continue")}
          </wow-button>
        `;

      case "error":
        return html`
          <h1 class="title">${t("emailConfirmation.error")}</h1>
          <p class="message error-message">${t("emailConfirmation.errorMessage")}</p>
        `;

      default:
        return html`<p class="message error-message">Unknown error</p>`;
    }
  }

  protected override render() {
    return html`
      <div class="container">
        <div class="logo">
          <wow-logo-full></wow-logo-full>
        </div>

        <div class="content">${this.renderContent()}</div>
      </div>
    `;
  }
}
